import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  PieChart, Pie, Cell, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Phone, PhoneCall, Target, Clock, TrendingUp,
  Download, DollarSign, Users, BarChart3, Loader2, Calculator
} from 'lucide-react';

axios.defaults.withCredentials = true;
const API = process.env.REACT_APP_BACKEND_URL;

const DISPOSITION_COLORS = {
  answered: '#22c55e',
  meeting_booked: '#3b82f6',
  demo_scheduled: '#6366f1',
  callback_requested: '#eab308',
  not_interested: '#f97316',
  voicemail_dropped: '#a855f7',
  no_answer: '#94a3b8',
  busy: '#ef4444',
  bad_number: '#dc2626',
  do_not_call: '#991b1b',
  disconnected_early: '#f59e0b',
  error: '#b91c1c',
  pending: '#cbd5e1',
  dialing: '#38bdf8',
  ringing: '#60a5fa',
};

const AREA_COLORS = {
  meetings_booked: '#3b82f6',
  answered: '#22c55e',
  voicemail_dropped: '#a855f7',
  no_answer: '#94a3b8',
  other: '#f59e0b',
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatHour = (hour) => {
  if (hour === 0) return '12a';
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return '12p';
  return `${hour - 12}p`;
};

const formatCurrency = (num) => {
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${Math.round(num).toLocaleString()}`;
};

export default function CallDispositionDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [roiInputs, setRoiInputs] = useState({
    monthlyAICost: 2500,
    numHumanSDRs: 3,
    avgSDRSalary: 70000,
    avgSDRCallsPerDay: 50,
    workingDaysPerMonth: 22,
  });

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/ai-calls/analytics/disposition-dashboard?days=${days}`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching disposition dashboard:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // ROI calculations
  const roi = useMemo(() => {
    if (!data) return null;
    const { monthlyAICost, numHumanSDRs, avgSDRSalary, avgSDRCallsPerDay, workingDaysPerMonth } = roiInputs;
    const s = data.summary;
    const rangeDays = data.date_range?.days || 30;

    // Scale to monthly
    const scaleFactor = 30 / rangeDays;
    const aiCallsPerMonth = Math.round(s.total_calls * scaleFactor);
    const aiMeetingsPerMonth = Math.round(s.meetings_booked * scaleFactor);

    const aiCostPerCall = aiCallsPerMonth > 0 ? monthlyAICost / aiCallsPerMonth : 0;
    const aiCostPerMeeting = aiMeetingsPerMonth > 0 ? monthlyAICost / aiMeetingsPerMonth : 0;

    const humanCallsPerMonth = numHumanSDRs * avgSDRCallsPerDay * workingDaysPerMonth;
    const humanMonthlyCost = (numHumanSDRs * avgSDRSalary * 1.3) / 12;
    const humanCostPerCall = humanCallsPerMonth > 0 ? humanMonthlyCost / humanCallsPerMonth : 0;
    // Assume 2% human conversion rate
    const humanMeetingsPerMonth = Math.round(humanCallsPerMonth * 0.02);
    const humanCostPerMeeting = humanMeetingsPerMonth > 0 ? humanMonthlyCost / humanMeetingsPerMonth : 0;

    const monthlySavings = humanMonthlyCost - monthlyAICost;
    const annualSavings = monthlySavings * 12;
    const roiPercent = monthlyAICost > 0 ? Math.round((annualSavings / (monthlyAICost * 12)) * 100) : 0;

    return {
      aiCallsPerMonth, aiMeetingsPerMonth, aiCostPerCall, aiCostPerMeeting,
      humanCallsPerMonth, humanMonthlyCost, humanCostPerCall, humanMeetingsPerMonth, humanCostPerMeeting,
      monthlySavings, annualSavings, roiPercent, monthlyAICost,
    };
  }, [data, roiInputs]);

  // Heatmap matrix
  const heatmapMatrix = useMemo(() => {
    if (!data?.hourly_heatmap) return null;
    // Build 7x24 matrix
    const matrix = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ total: 0, answered: 0, rate: 0 })));
    for (const cell of data.hourly_heatmap) {
      matrix[cell.day][cell.hour] = { total: cell.total, answered: cell.answered, rate: cell.rate };
    }
    return matrix;
  }, [data]);

  const exportCSV = () => {
    if (!data) return;
    const s = data.summary;
    let csv = 'Call Disposition Dashboard Report\n';
    csv += `Date Range,${data.date_range.start} to ${data.date_range.end}\n\n`;
    csv += 'Summary\n';
    csv += `Total Calls,${s.total_calls}\nConnects,${s.connects}\nMeetings Booked,${s.meetings_booked}\n`;
    csv += `Answer Rate,${s.answer_rate}%\nConversion Rate,${s.conversion_rate}%\nAvg Duration,${formatDuration(s.avg_duration_seconds)}\n\n`;
    csv += 'Disposition Breakdown\nStatus,Count,Percentage\n';
    for (const d of data.disposition_breakdown) {
      csv += `${d.status.replace(/_/g, ' ')},${d.count},${d.percentage}%\n`;
    }
    csv += '\nCampaign Comparison\nName,Calls,Connects,Meetings,Answer Rate,Conversion\n';
    for (const c of data.campaign_comparison) {
      csv += `${c.name},${c.total_calls},${c.connects},${c.meetings_booked},${c.answer_rate}%,${c.conversion_rate}%\n`;
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-disposition-${data.date_range.start}-to-${data.date_range.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const exportExcel = () => {
    if (!data) return;
    const s = data.summary;
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Metric', 'Value'],
      ['Total Calls', s.total_calls],
      ['Connects', s.connects],
      ['Meetings Booked', s.meetings_booked],
      ['Answer Rate', `${s.answer_rate}%`],
      ['Conversion Rate', `${s.conversion_rate}%`],
      ['Avg Duration', formatDuration(s.avg_duration_seconds)],
      ['Voicemails Dropped', s.voicemails_dropped],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');

    // Disposition sheet
    const dispData = [['Status', 'Count', 'Percentage'], ...data.disposition_breakdown.map(d => [d.status.replace(/_/g, ' '), d.count, `${d.percentage}%`])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(dispData), 'Dispositions');

    // Campaign sheet
    const campData = [['Campaign', 'Calls', 'Connects', 'Meetings', 'Answer Rate', 'Conversion'],
      ...data.campaign_comparison.map(c => [c.name, c.total_calls, c.connects, c.meetings_booked, `${c.answer_rate}%`, `${c.conversion_rate}%`])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(campData), 'Campaigns');

    XLSX.writeFile(wb, `call-disposition-${data.date_range.start}-to-${data.date_range.end}.xlsx`);
    toast.success('Excel exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const s = data?.summary;

  return (
    <div className="space-y-6 p-1">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Call Disposition Dashboard</h1>
          <p className="text-sm text-slate-500">
            {data?.date_range?.start} to {data?.date_range?.end}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date range tabs */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            {[7, 14, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  days === d ? 'bg-white shadow text-blue-600 font-medium' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel}>
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard icon={Phone} label="Total Calls" value={s?.total_calls || 0} color="blue" />
        <KPICard icon={PhoneCall} label="Connects" value={s?.connects || 0} sub={`${s?.answer_rate || 0}% rate`} color="green" />
        <KPICard icon={Target} label="Meetings Booked" value={s?.meetings_booked || 0} sub={`${s?.conversion_rate || 0}% conv`} color="purple" />
        <KPICard icon={TrendingUp} label="Answer Rate" value={`${s?.answer_rate || 0}%`} color="emerald" />
        <KPICard icon={Target} label="Conversion" value={`${s?.conversion_rate || 0}%`} color="orange" />
        <KPICard icon={Clock} label="Avg Duration" value={formatDuration(s?.avg_duration_seconds)} color="slate" />
      </div>

      {/* Disposition Breakdown + Calls Over Time */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Donut Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Call Dispositions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.disposition_breakdown?.length > 0 ? (
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="w-64 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.disposition_breakdown}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {data.disposition_breakdown.map((entry) => (
                          <Cell key={entry.status} fill={DISPOSITION_COLORS[entry.status] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name.replace(/_/g, ' ')]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5 w-full">
                  {data.disposition_breakdown.map((d) => (
                    <div key={d.status} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: DISPOSITION_COLORS[d.status] || '#94a3b8' }} />
                        <span className="capitalize">{d.status.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">{d.count}</span>
                        <span className="text-slate-400 w-12 text-right">{d.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">No call data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Area Chart - Calls Over Time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Calls Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.daily_timeline?.length > 0 ? (
              <ResponsiveContainer width="100%" height={264}>
                <AreaChart data={data.daily_timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(d) => d} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="meetings_booked" name="Meetings" stackId="1" stroke={AREA_COLORS.meetings_booked} fill={AREA_COLORS.meetings_booked} fillOpacity={0.8} />
                  <Area type="monotone" dataKey="answered" name="Answered" stackId="1" stroke={AREA_COLORS.answered} fill={AREA_COLORS.answered} fillOpacity={0.8} />
                  <Area type="monotone" dataKey="voicemail_dropped" name="Voicemail" stackId="1" stroke={AREA_COLORS.voicemail_dropped} fill={AREA_COLORS.voicemail_dropped} fillOpacity={0.8} />
                  <Area type="monotone" dataKey="no_answer" name="No Answer" stackId="1" stroke={AREA_COLORS.no_answer} fill={AREA_COLORS.no_answer} fillOpacity={0.8} />
                  <Area type="monotone" dataKey="other" name="Other" stackId="1" stroke={AREA_COLORS.other} fill={AREA_COLORS.other} fillOpacity={0.8} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">No call data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign Comparison */}
      {data?.campaign_comparison?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              Campaign Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.campaign_comparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="total_calls" name="Total Calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="connects" name="Connects" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="meetings_booked" name="Meetings" fill="#a855f7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Connects</TableHead>
                  <TableHead className="text-right">Meetings</TableHead>
                  <TableHead className="text-right">Answer Rate</TableHead>
                  <TableHead className="text-right">Conversion</TableHead>
                  <TableHead className="text-right">Avg Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.campaign_comparison.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-right">{c.total_calls}</TableCell>
                    <TableCell className="text-right">{c.connects}</TableCell>
                    <TableCell className="text-right text-purple-600 font-medium">{c.meetings_booked}</TableCell>
                    <TableCell className="text-right">
                      <span className={c.answer_rate >= 30 ? 'text-green-600' : c.answer_rate >= 15 ? 'text-yellow-600' : 'text-red-600'}>
                        {c.answer_rate}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={c.conversion_rate >= 20 ? 'text-green-600' : c.conversion_rate >= 10 ? 'text-yellow-600' : 'text-orange-600'}>
                        {c.conversion_rate}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{formatDuration(c.avg_duration_seconds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Best Time to Call Heatmap */}
      {heatmapMatrix && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-emerald-500" />
              Best Time to Call
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="min-w-[700px]">
                {/* Hour headers */}
                <div className="flex mb-1">
                  <div className="w-10 shrink-0" />
                  {Array.from({ length: 14 }, (_, i) => i + 7).map((h) => (
                    <div key={h} className="flex-1 text-center text-[10px] text-slate-400">
                      {formatHour(h)}
                    </div>
                  ))}
                </div>
                {/* Day rows (business hours 7am-8pm) */}
                {DAY_NAMES.map((dayName, dayIdx) => (
                  <div key={dayIdx} className="flex mb-1 items-center">
                    <div className="w-10 shrink-0 text-xs text-slate-500 font-medium">{dayName}</div>
                    {Array.from({ length: 14 }, (_, i) => i + 7).map((h) => {
                      const cell = heatmapMatrix[dayIdx][h];
                      return (
                        <div
                          key={h}
                          className="flex-1 aspect-square mx-0.5 rounded-sm cursor-default relative group"
                          style={{
                            backgroundColor: cell.total > 0
                              ? `rgba(34, 197, 94, ${Math.max(cell.rate / 100, 0.08)})`
                              : '#f8fafc',
                            border: '1px solid #e2e8f0',
                          }}
                          title={`${dayName} ${formatHour(h)}: ${cell.total} calls, ${cell.answered} answered (${cell.rate}%)`}
                        >
                          {cell.total > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center text-[9px] font-medium text-slate-700">
                              {cell.total}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-3 justify-end">
                  <span className="text-[10px] text-slate-400">Low</span>
                  <div className="flex gap-0.5">
                    {[0.08, 0.2, 0.4, 0.6, 0.8, 1].map((opacity) => (
                      <div key={opacity} className="w-4 h-3 rounded-sm" style={{ backgroundColor: `rgba(34, 197, 94, ${opacity})` }} />
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400">High answer rate</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ROI Calculator */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-green-600" />
            ROI Calculator: AI Agent vs Human SDR Team
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Editable Inputs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div>
              <Label className="text-xs text-slate-500">Monthly AI Cost</Label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  className="pl-8"
                  value={roiInputs.monthlyAICost}
                  onChange={(e) => setRoiInputs(p => ({ ...p, monthlyAICost: Number(e.target.value) }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">SDRs Replaced</Label>
              <Input
                type="number"
                value={roiInputs.numHumanSDRs}
                onChange={(e) => setRoiInputs(p => ({ ...p, numHumanSDRs: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Avg SDR Salary/yr</Label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  className="pl-8"
                  value={roiInputs.avgSDRSalary}
                  onChange={(e) => setRoiInputs(p => ({ ...p, avgSDRSalary: Number(e.target.value) }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">SDR Calls/Day</Label>
              <Input
                type="number"
                value={roiInputs.avgSDRCallsPerDay}
                onChange={(e) => setRoiInputs(p => ({ ...p, avgSDRCallsPerDay: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Working Days/Mo</Label>
              <Input
                type="number"
                value={roiInputs.workingDaysPerMonth}
                onChange={(e) => setRoiInputs(p => ({ ...p, workingDaysPerMonth: Number(e.target.value) }))}
              />
            </div>
          </div>

          {/* Side-by-side Comparison */}
          {roi && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* AI Agent */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h3 className="font-bold text-blue-800 text-lg mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> AI Agent
                </h3>
                <div className="space-y-3">
                  <ROIStat label="Monthly Cost" value={formatCurrency(roi.monthlyAICost)} />
                  <ROIStat label="Calls/Month" value={roi.aiCallsPerMonth.toLocaleString()} />
                  <ROIStat label="Meetings/Month" value={roi.aiMeetingsPerMonth.toLocaleString()} />
                  <ROIStat label="Cost per Call" value={`$${roi.aiCostPerCall.toFixed(2)}`} highlight />
                  <ROIStat label="Cost per Meeting" value={`$${roi.aiCostPerMeeting.toFixed(2)}`} highlight />
                </div>
              </div>

              {/* Human SDR Team */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 text-lg mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5" /> Human SDR Team ({roiInputs.numHumanSDRs} SDRs)
                </h3>
                <div className="space-y-3">
                  <ROIStat label="Monthly Cost" value={formatCurrency(roi.humanMonthlyCost)} />
                  <ROIStat label="Calls/Month" value={roi.humanCallsPerMonth.toLocaleString()} />
                  <ROIStat label="Meetings/Month (est. 2%)" value={roi.humanMeetingsPerMonth.toLocaleString()} />
                  <ROIStat label="Cost per Call" value={`$${roi.humanCostPerCall.toFixed(2)}`} highlight />
                  <ROIStat label="Cost per Meeting" value={`$${roi.humanCostPerMeeting.toFixed(2)}`} highlight />
                </div>
              </div>
            </div>
          )}

          {/* Savings Banner */}
          {roi && roi.monthlySavings > 0 && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm text-green-700">Monthly Savings</div>
                  <div className="text-2xl font-bold text-green-800">{formatCurrency(roi.monthlySavings)}</div>
                </div>
                <div>
                  <div className="text-sm text-green-700">Annual Savings</div>
                  <div className="text-2xl font-bold text-green-800">{formatCurrency(roi.annualSavings)}</div>
                </div>
                <div>
                  <div className="text-sm text-green-700">ROI</div>
                  <div className="text-2xl font-bold text-green-800">{roi.roiPercent}%</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color }) {
  const colorMap = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    emerald: 'text-emerald-500',
    orange: 'text-orange-500',
    slate: 'text-slate-500',
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <Icon className={`h-7 w-7 opacity-40 ${colorMap[color] || 'text-slate-400'}`} />
        </div>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ROIStat({ label, value, highlight }) {
  return (
    <div className={`flex items-center justify-between ${highlight ? 'bg-white/60 rounded-lg px-3 py-2' : ''}`}>
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`font-semibold ${highlight ? 'text-lg' : ''}`}>{value}</span>
    </div>
  );
}
